
import React, { useState, useMemo, useEffect } from 'react';
import {
  PlusCircle, List, LayoutGrid, ChevronLeft, Save, XCircle, FileEdit
} from 'lucide-react';
import Button from '../../components/ui/Button';
import SearchInput from "../../components/ui/SearchInput";
import api from "../../api/api";



const fetchAllEmployees = async () => {
  setIsLoading(true);
  try {
    const data = await api.get("EmployeeListAPI", { UserName: "Admin" });
    setEmployees(data?.EmployeeListAPIjs?.Table?.map(mapEmployeeFromApi) || []);
  } catch (error) {
    console.error(error);
    alert("Could not fetch employee list.");
  } finally { setIsLoading(false); }
};

  const handleSave = async (employeeData) => {
  try {
    const payload = mapEmployeeToApi(employeeData);
    const response = await api.post("SaveEmployeeAPI", payload);
    if (response?.SaveEmployeeAPIjs?.Table?.length) {
      alert('✅ Employee saved successfully!');
      await fetchAllEmployees();
      setPage({ view: 'list', data: null });
    } else throw new Error("Failed to save");
  } catch (error) {
    console.error(error);
    alert('❌ Failed to save employee.');
  }
};

const handleEdit = async (employee) => {
  try {
    const res = await api.EditEmployee(employee.Eid); // returns { Table: [...] }
    const table = res?.Table || [];
    if (table.length) {
      setPage({ view: 'edit', data: mapEmployeeFromApi(table[0]) });
    } else {
      throw new Error("Employee not found");
    }
  } catch (error) {
    console.error(error);
    alert('❌ Could not fetch employee details.');
  }
};

// --- DATA MAPPING ---
const mapEmployeeFromApi = (emp) => ({
  Eid: emp.Eid,
  employeeCode: emp.Ecode,
  employeeName: emp.Ename,
  gender: (emp.Gender || "Male").toLowerCase() === "male" ? "Male" :
    (emp.Gender?.toLowerCase() === "female" ? "Female" : "Other"),
  designation: emp.Designation || '',
  department: emp.Department || '',
  dateOfBirth: emp.Dob ? emp.Dob.split("T")[0] : "",
  dateOfJoin: emp.Doj ? emp.Doj.split("T")[0] : "",
  address: emp.Address || '',
  city: emp.City || '',
  phone: emp.Phone || '',
  mobile: emp.mobile || emp.Mobile || '',
  email: emp.Emailid || '',
  remark: emp.Remark || '',
  status: emp.Status === "1",
  basic: emp.Basic || 0,
  da: emp.DA || 0,
  hra: emp.HRA || 0,
  allowance: emp.Allowances || 0,
  allowanceMode: emp.AllowanceMode || 'Monthly',
  paymentMode: (emp.paymentmode || 'Bank Transfer').toLowerCase() === "cash" ? "Cash" :
    (emp.paymentmode?.toLowerCase() === "upi" ? "UPI" : "Bank Transfer"),
  gpayPhonepeNo: emp.GpayPhonepayNo || '',
  bankName: emp.Bankname || '',
  branchName: emp.Branchname || '',
  ifscCode: emp.Ifsccode || '',
  accNo: emp.Accno || '',
});

const mapEmployeeToApi = (emp) => ({
  Eid: emp.Eid || 0,
  Ecode: emp.employeeCode,
  Ename: emp.employeeName,
  Designation: emp.designation,
  Department: emp.department,
  Dob: emp.dateOfBirth,
  Doj: emp.dateOfJoin,
  Address: emp.address,
  City: emp.city,
  Phone: emp.phone,
  mobile: emp.mobile,
  Emailid: emp.email,
  Status: emp.status ? "1" : "0",
  SqlAction: emp.Eid ? "update" : "insert",
  Basic: emp.basic || 0,
  DA: emp.da || 0,
  HRA: emp.hra || 0,
  Allowances: emp.allowance || 0,
  Gender: emp.gender,
  Remark: emp.remark,
  paymentmode: emp.paymentMode.toLowerCase(),
  GpayPhonepayNo: emp.gpayPhonepeNo,
  Bankname: emp.bankName,
  Branchname: emp.branchName,
  Ifsccode: emp.ifscCode,
  Accno: emp.accNo,
  AllowanceMode: emp.allowanceMode,
  UserName: "Admin",
});

// --- TABLE VIEW ---
const TableView = ({ employees, onEdit }) => (
  <div className="overflow-x-auto bg-white rounded-lg shadow-md">
    <table className="w-full border-collapse">
      <thead>
        <tr className='bg-slate-50'>
          {['Name', 'Designation', 'Mobile', 'Email', 'Status', 'Actions'].map(title => (
            <th key={title} className="p-4 text-left text-sm font-medium text-grey-1000 border-b border-slate-200">{title}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {employees.map(emp => (
          <tr key={emp.Eid} className="hover:bg-slate-50 border-b border-slate-200 last:border-b-0">
            {/* <td className="p-4">{emp.employeeCode}</td> */}
            <td className="p-4 font-medium text-slate-800">{emp.employeeName}</td>
            <td className="p-4 text-slate-600">{emp.designation}</td>
            <td className="p-4 text-slate-600">{emp.mobile || 'N/A'}</td>
            <td className="p-4 text-slate-600">{emp.email || 'N/A'}</td>
            <td className="p-4">
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${emp.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {emp.status ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td className="p-4">
              <button
                onClick={() => onEdit(emp)}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition"
              >
                <FileEdit className="w-5 h-5" /> Edit
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// --- CARD VIEW ---
const CardView = ({ employees, onEdit }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
    {employees.map(emp => (
      <div
        key={emp.Eid}
        className="bg-white rounded-lg shadow-md p-6 hover:-translate-y-1 hover:shadow-xl transition"
      >
        <h3 className="text-lg font-semibold text-black-500 border-b pb-2 mb-4">{emp.employeeName}</h3>
        <div className="space-y-2 text-sm text-slate-600">
          <p><strong>Employee Code:</strong> {emp.employeeCode || "N/A"}</p>
          <p><strong>Mobile:</strong> {emp.mobile || "N/A"}</p>
          <p><strong>Email:</strong> {emp.email || "N/A"}</p>
          <button
            onClick={() => onEdit(emp)}
            className="flex items-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition"
          >
            <FileEdit className="w-5 h-5" /> Edit
          </button>
        </div>
      </div>
    ))}
  </div>
);

// --- EMPLOYEE LIST WRAPPER ---
const EmployeeList = ({ employees, view, onEdit }) => {
  if (!employees.length) return <div className="text-center text-lg text-slate-500 p-12 bg-white rounded-lg shadow-md"> No employees found.</div>;
  return view === 'table' ? <TableView employees={employees} onEdit={onEdit} /> : <CardView employees={employees} onEdit={onEdit} />;
};

// --- LIST PAGE ---
const ListPage = ({ employees, view, setView, onEdit, onAddNew }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredEmployees = useMemo(() =>
    (Array.isArray(employees) ? employees : []).filter(emp =>
      emp.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.designation.toLowerCase().includes(searchTerm.toLowerCase())
    ), [employees, searchTerm]);

  return (
    <div className="flex flex-col h-full min-h-screen px-4 sm:px-6">
      <div className="flex flex-col gap-3 mt-0">
        <h2 className=" mt-0 text-3xl font-bold text-center text-slate-800">Employee Management</h2>

        <div className="flex items-center justify-between mt-2">
          <Button
            className="flex items-center gap-2 px-3 py-2 text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow hover:from-blue-600 hover:to-indigo-700"
            onClick={onAddNew}
          >
            <PlusCircle size={18} /> New
          </Button>

          <div className="flex items-center p-1 bg-gray-100 rounded-lg shadow-sm">
            <button onClick={() => setView("card")} className={`p-2 rounded ${view === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}><LayoutGrid size={18} /></button>
            <button onClick={() => setView("table")} className={`p-2 rounded ${view === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}><List size={18} /></button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2 bg-gray  rounded-lg px-2 py-2">

         {/* <Button
            variant="outline"
            className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
            onClick={() => setView("home")}
          >
            ⬅
          </Button> */}

          <SearchInput
            placeholder="Search by Name, Code, or Designation..."
            className="flex-1"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-4">
        <EmployeeList employees={filteredEmployees} view={view} onEdit={onEdit} />
      </div>
    </div>
  );
};


const EmployeeForm = ({ initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState(initialData);
  const isEditing = !!initialData.employeeCode;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.employeeCode || !formData.employeeName || !formData.dateOfBirth) {
      alert('Please fill all required fields (*)');
      return;
    }
    onSave(formData);
  };

  const inputClasses = "w-full p-2.5 border border-slate-300 rounded-md text-base transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:cursor-not-allowed";
  const labelClasses = "font-medium text-slate-600 text-sm";
  const requiredLabel = <span className="text-red-500"> *</span>;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Left Column */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Personal & Work Details</h4>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Employee Code{requiredLabel}</label><input type="text" name="employeeCode" value={formData.employeeCode} onChange={handleChange} required disabled={isEditing} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Employee Name{requiredLabel}</label><input type="text" name="employeeName" value={formData.employeeName} onChange={handleChange} required className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Gender</label><select name="gender" value={formData.gender} onChange={handleChange} className={inputClasses}><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Designation</label><input type="text" name="designation" value={formData.designation} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Department</label><input type="text" name="department" value={formData.department} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Date Of Birth{requiredLabel}</label><input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Date Of Join</label><input type="date" name="dateOfJoin" value={formData.dateOfJoin} onChange={handleChange} className={inputClasses} /></div>
          </div>
          <div className="flex flex-col gap-4">
            <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Contact Details</h4>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Address</label><textarea name="address" value={formData.address} onChange={handleChange} className={`${inputClasses} min-h-[80px]`}></textarea></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>City</label><input type="text" name="city" value={formData.city} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Mobile</label><input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Email Id</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Remark</label><textarea name="remark" value={formData.remark} onChange={handleChange} className={`${inputClasses} min-h-[80px]`}></textarea></div>
            <div className="flex flex-row items-center gap-4">
              <label className={`${labelClasses}`}>Status{requiredLabel}</label>
              <label className="relative inline-block w-[50px] h-[28px]">
                <input type="checkbox" name="status" checked={formData.status} onChange={handleChange} className="opacity-0 w-0 h-0 peer" />
                <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-gray-300 rounded-full transition-all before:absolute before:h-5 before:w-5 before:left-1 before:bottom-1 before:bg-white before:rounded-full before:transition-all peer-checked:bg-green-500 peer-checked:before:translate-x-[22px]"></span>
              </label>
              <span className="font-bold">{formData.status ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Salary Details</h4>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Basic</label><input type="number" step="0.01" name="basic" value={formData.basic} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>D/A</label><input type="number" step="0.01" name="da" value={formData.da} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>HRA</label><input type="number" step="0.01" name="hra" value={formData.hra} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Allowance</label><input type="number" step="0.01" name="allowance" value={formData.allowance} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Allowance Mode</label><select name="allowanceMode" value={formData.allowanceMode} onChange={handleChange} className={inputClasses}><option>Monthly</option><option>Yearly</option></select></div>
          </div>
          <div className="flex flex-col gap-4">
            <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Payment Details</h4>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Payment Mode</label><select name="paymentMode" value={formData.paymentMode} onChange={handleChange} className={inputClasses}><option>Bank Transfer</option><option>UPI</option><option>Cash</option></select></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Gpay & Phonepe No</label><input type="text" name="gpayPhonepeNo" value={formData.gpayPhonepeNo} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Bank Name</label><input type="text" name="bankName" value={formData.bankName} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Branch Name</label><input type="text" name="branchName" value={formData.branchName} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>IFSC Code</label><input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} className={inputClasses} /></div>
            <div className="flex flex-col gap-1.5"><label className={labelClasses}>Account No</label><input type="text" name="accNo" value={formData.accNo} onChange={handleChange} className={inputClasses} /></div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-slate-200">
        <button type="submit" className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
          Save
        </button>
        <button type="button" className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600" onClick={onCancel}>
          Cancel
        </button>

      </div>
    </form>
  );
};

const EntryPage = ({ onSave, onCancel, initialData }) => {
  const isEditing = !!initialData;
  const today = new Date().toISOString().split('T')[0];
  const newEmployeeTemplate = {
    employeeCode: '', employeeName: '', gender: 'Male', designation: '', department: '',
    dateOfBirth: '', dateOfJoin: today, address: '', city: '', phone: '', mobile: '',
    email: '', remark: '', status: true, basic: 0, da: 0, hra: 0, allowance: 0,
    allowanceMode: 'Monthly', paymentMode: 'Bank Transfer', gpayPhonepeNo: '',
    bankName: '', branchName: '', ifscCode: '', accNo: '',
  };

  return (
    <div className="max-w-5xl mx-auto bg-white p-6 sm:p-10 rounded-lg shadow-md">
      <div className="flex items-center gap-4 mb-8">
        {/* <button className="w-10 h-10 flex items-center justify-center bg-slate-200 rounded-full transition-colors hover:bg-slate-300" onClick={onCancel} aria-label="Go back to list">
          <ChevronLeft />
        </button> */}
        <h2 className="text-2xl font-bold text-center text-slate-800">{isEditing ? 'Edit Employee' : 'Create New Employee'}</h2>
      </div>
      <EmployeeForm
        initialData={isEditing ? initialData : newEmployeeTemplate}
        onSave={onSave}
        onCancel={onCancel}
      />
    </div>
  );
};



// --- MAIN APP ---
export default function App() {
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState({ view: 'list', data: null });
  const [view, setView] = useState('card');
  
  const [isLoading, setIsLoading] = useState(true);

  const fetchAllEmployees = async () => {
  setIsLoading(true);
  try {
    const data = await api.EmployeeList("Admin");
    // data.Table exists directly
    const table = data?.Table || [];
    setEmployees(table.map(mapEmployeeFromApi));
  } catch (error) {
    console.error(error);
    alert("Could not fetch employee list.");
  } finally {
    setIsLoading(false);
  }
};


  useEffect(() => { fetchAllEmployees(); }, []);

  const handleSave = async (employeeData) => {
    try {
      const payload = mapEmployeeToApi(employeeData);
      const response = await api.SaveEmployee(payload);
      if (response?.SaveEmployeeAPIjs?.Table?.length) {
        alert('✅ Employee saved successfully!');
        await fetchAllEmployees();
        setPage({ view: 'list', data: null });
      } else throw new Error("Failed to save");
    } catch (error) {
      console.error(error);
      alert('❌ Failed to save employee.');
    }
  };

 const handleEdit = async (employee) => {
  try {
    const res = await api.EditEmployee(employee.Eid); // should return { Table: [...] }
    const table = res?.Table || []; // <-- use Table directly
    if (table.length) {
      setPage({ view: 'edit', data: mapEmployeeFromApi(table[0]) });
    } else {
      throw new Error("Employee not found");
    }
  } catch (error) {
    console.error(error);
    alert('❌ Could not fetch employee details.');
  }
};


  const handleAddNew = () => setPage({ view: 'create', data: null });
  const handleCancel = () => setPage({ view: 'list', data: null });

  const renderPage = () => {
    if (isLoading && page.view === 'list') return <div className="text-center p-12">Loading...</div>;
    switch (page.view) {
      case 'create':
      case 'edit': return <EntryPage onSave={handleSave} onCancel={handleCancel} initialData={page.data} />;
      case 'list':
      default: return <ListPage employees={employees} view={view} setView={setView} onEdit={handleEdit} onAddNew={handleAddNew}  onBack={handleCancel}/>;
    }
  };

  return (
    <div className="flex flex-col min-h-screen font-sans bg-slate-100 text-slate-800">
      <main className="flex-grow p-4 sm:p-8 max-w-7xl mx-auto w-full">
        {renderPage()}
      </main>
    </div>
  );
}



// import React, { useState, useMemo, useEffect } from 'react';
// import {
//   PlusCircle, List, LayoutGrid, ChevronLeft, Save, XCircle, FileEdit
// } from 'lucide-react';
// import Button from '../../components/ui/Button';
// import SearchInput from "../../components/ui/SearchInput";

// // --- API CONFIG & FUNCTIONS ---
// const API_BASE_URL = "https://tradeservice.revampapps.com";

// const api = {
//   EmployeeList: async (UserName) => {
//     const response = await fetch(`${API_BASE_URL}/EmployeeListAPI?UserName=${UserName}`);
//     if (!response.ok) throw new Error('Network response was not ok');
//     return response.json();
//   },
//   SaveEmployee: async (payload) => {
//     const response = await fetch(`${API_BASE_URL}/SaveEmployeeAPI`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });
//     if (!response.ok) throw new Error('Network response was not ok');
//     return response.json();
//   },
//   EditEmployee: async (PrimeIdStr) => {
//     const response = await fetch(`${API_BASE_URL}/EditEmployeeAPI?PrimeIdStr=${PrimeIdStr}`);
//     if (!response.ok) throw new Error('Network response was not ok');
//     return response.json();
//   },
// };

// // --- DATA MAPPING ---
// const mapEmployeeFromApi = (emp) => ({
//   Eid: emp.Eid,
//   employeeCode: emp.Ecode,
//   employeeName: emp.Ename,
//   gender: (emp.Gender || "Male").toLowerCase() === "male" ? "Male" :
//     (emp.Gender?.toLowerCase() === "female" ? "Female" : "Other"),
//   designation: emp.Designation || '',
//   department: emp.Department || '',
//   dateOfBirth: emp.Dob ? emp.Dob.split("T")[0] : "",
//   dateOfJoin: emp.Doj ? emp.Doj.split("T")[0] : "",
//   address: emp.Address || '',
//   city: emp.City || '',
//   phone: emp.Phone || '',
//   mobile: emp.mobile || emp.Mobile || '',
//   email: emp.Emailid || '',
//   remark: emp.Remark || '',
//   status: emp.Status === "1",
//   basic: emp.Basic || 0,
//   da: emp.DA || 0,
//   hra: emp.HRA || 0,
//   allowance: emp.Allowances || 0,
//   allowanceMode: emp.AllowanceMode || 'Monthly',
//   paymentMode: (emp.paymentmode || 'Bank Transfer').toLowerCase() === "cash" ? "Cash" :
//     (emp.paymentmode?.toLowerCase() === "upi" ? "UPI" : "Bank Transfer"),
//   gpayPhonepeNo: emp.GpayPhonepayNo || '',
//   bankName: emp.Bankname || '',
//   branchName: emp.Branchname || '',
//   ifscCode: emp.Ifsccode || '',
//   accNo: emp.Accno || '',
// });

// const mapEmployeeToApi = (emp) => ({
//   Eid: emp.Eid || 0,
//   Ecode: emp.employeeCode,
//   Ename: emp.employeeName,
//   Designation: emp.designation,
//   Department: emp.department,
//   Dob: emp.dateOfBirth,
//   Doj: emp.dateOfJoin,
//   Address: emp.address,
//   City: emp.city,
//   Phone: emp.phone,
//   mobile: emp.mobile,
//   Emailid: emp.email,
//   Status: emp.status ? "1" : "0",
//   SqlAction: emp.Eid ? "update" : "insert",
//   Basic: emp.basic || 0,
//   DA: emp.da || 0,
//   HRA: emp.hra || 0,
//   Allowances: emp.allowance || 0,
//   Gender: emp.gender,
//   Remark: emp.remark,
//   paymentmode: emp.paymentMode.toLowerCase(),
//   GpayPhonepayNo: emp.gpayPhonepeNo,
//   Bankname: emp.bankName,
//   Branchname: emp.branchName,
//   Ifsccode: emp.ifscCode,
//   Accno: emp.accNo,
//   AllowanceMode: emp.allowanceMode,
//   UserName: "Admin",
// });

// // --- TABLE VIEW ---
// const TableView = ({ employees, onEdit }) => (
//   <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//     <table className="w-full border-collapse">
//       <thead>
//         <tr className='bg-slate-50'>
//           {['Name', 'Designation', 'Mobile', 'Email', 'Status', 'Actions'].map(title => (
//             <th key={title} className="p-4 text-left text-sm font-medium text-grey-1000 border-b border-slate-200">{title}</th>
//           ))}
//         </tr>
//       </thead>
//       <tbody>
//         {employees.map(emp => (
//           <tr key={emp.Eid} className="hover:bg-slate-50 border-b border-slate-200 last:border-b-0">
//             {/* <td className="p-4">{emp.employeeCode}</td> */}
//             <td className="p-4 font-medium text-slate-800">{emp.employeeName}</td>
//             <td className="p-4 text-slate-600">{emp.designation}</td>
//             <td className="p-4 text-slate-600">{emp.mobile || 'N/A'}</td>
//             <td className="p-4 text-slate-600">{emp.email || 'N/A'}</td>
//             <td className="p-4">
//               <span className={`px-2 py-1 text-xs font-semibold rounded-full ${emp.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
//                 {emp.status ? 'Active' : 'Inactive'}
//               </span>
//             </td>
//             <td className="p-4">
//               <button
//                 onClick={() => onEdit(emp)}
//                 className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition"
//               >
//                 <FileEdit className="w-5 h-5" /> Edit
//               </button>
//             </td>
//           </tr>
//         ))}
//       </tbody>
//     </table>
//   </div>
// );

// // --- CARD VIEW ---
// const CardView = ({ employees, onEdit }) => (
//   <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
//     {employees.map(emp => (
//       <div
//         key={emp.Eid}
//         className="bg-white rounded-lg shadow-md p-6 hover:-translate-y-1 hover:shadow-xl transition"
//       >
//         <h3 className="text-lg font-semibold text-black-500 border-b pb-2 mb-4">{emp.employeeName}</h3>
//         <div className="space-y-2 text-sm text-slate-600">
//           <p><strong>Employee Code:</strong> {emp.employeeCode || "N/A"}</p>
//           <p><strong>Mobile:</strong> {emp.mobile || "N/A"}</p>
//           <p><strong>Email:</strong> {emp.email || "N/A"}</p>
//           <button
//             onClick={() => onEdit(emp)}
//             className="flex items-center gap-2 px-3 py-1.5 mt-3 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition"
//           >
//             <FileEdit className="w-5 h-5" /> Edit
//           </button>
//         </div>
//       </div>
//     ))}
//   </div>
// );

// // --- EMPLOYEE LIST WRAPPER ---
// const EmployeeList = ({ employees, view, onEdit }) => {
//   if (!employees.length) return <div className="text-center text-lg text-slate-500 p-12 bg-white rounded-lg shadow-md"> No employees found.</div>;
//   return view === 'table' ? <TableView employees={employees} onEdit={onEdit} /> : <CardView employees={employees} onEdit={onEdit} />;
// };

// // --- LIST PAGE ---
// const ListPage = ({ employees, view, setView, onEdit, onAddNew }) => {
//   const [searchTerm, setSearchTerm] = useState("");

//   const filteredEmployees = useMemo(() =>
//     (Array.isArray(employees) ? employees : []).filter(emp =>
//       emp.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       emp.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       emp.designation.toLowerCase().includes(searchTerm.toLowerCase())
//     ), [employees, searchTerm]);

//   return (
//     <div className="flex flex-col h-full min-h-screen px-4 sm:px-6">
//       <div className="flex flex-col gap-3 mt-0">
//         <h2 className=" mt-0 text-3xl font-bold text-center text-slate-800">Employee Management</h2>

//         <div className="flex items-center justify-between mt-2">
//           <Button
//             className="flex items-center gap-2 px-3 py-2 text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow hover:from-blue-600 hover:to-indigo-700"
//             onClick={onAddNew}
//           >
//             <PlusCircle size={18} /> New
//           </Button>

//           <div className="flex items-center p-1 bg-gray-100 rounded-lg shadow-sm">
//             <button onClick={() => setView("card")} className={`p-2 rounded ${view === "card" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}><LayoutGrid size={18} /></button>
//             <button onClick={() => setView("table")} className={`p-2 rounded ${view === "table" ? "bg-white text-blue-600 shadow" : "text-gray-500"}`}><List size={18} /></button>
//           </div>
//         </div>

//         <div className="flex items-center gap-2 mt-2 bg-gray  rounded-lg px-2 py-2">

//          {/* <Button
//             variant="outline"
//             className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-full"
//             onClick={() => setView("home")}
//           >
//             ⬅
//           </Button> */}

//           <SearchInput
//             placeholder="Search by Name, Code, or Designation..."
//             className="flex-1"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//         </div>
//       </div>

//       <div className="mt-4">
//         <EmployeeList employees={filteredEmployees} view={view} onEdit={onEdit} />
//       </div>
//     </div>
//   );
// };


// const EmployeeForm = ({ initialData, onSave, onCancel }) => {
//   const [formData, setFormData] = useState(initialData);
//   const isEditing = !!initialData.employeeCode;

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData(prev => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : value,
//     }));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!formData.employeeCode || !formData.employeeName || !formData.dateOfBirth) {
//       alert('Please fill all required fields (*)');
//       return;
//     }
//     onSave(formData);
//   };

//   const inputClasses = "w-full p-2.5 border border-slate-300 rounded-md text-base transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-50 disabled:cursor-not-allowed";
//   const labelClasses = "font-medium text-slate-600 text-sm";
//   const requiredLabel = <span className="text-red-500"> *</span>;

//   return (
//     <form onSubmit={handleSubmit} className="flex flex-col gap-8">
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
//         {/* Left Column */}
//         <div className="flex flex-col gap-6">
//           <div className="flex flex-col gap-4">
//             <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Personal & Work Details</h4>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Employee Code{requiredLabel}</label><input type="text" name="employeeCode" value={formData.employeeCode} onChange={handleChange} required disabled={isEditing} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Employee Name{requiredLabel}</label><input type="text" name="employeeName" value={formData.employeeName} onChange={handleChange} required className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Gender</label><select name="gender" value={formData.gender} onChange={handleChange} className={inputClasses}><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Designation</label><input type="text" name="designation" value={formData.designation} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Department</label><input type="text" name="department" value={formData.department} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Date Of Birth{requiredLabel}</label><input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} required className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Date Of Join</label><input type="date" name="dateOfJoin" value={formData.dateOfJoin} onChange={handleChange} className={inputClasses} /></div>
//           </div>
//           <div className="flex flex-col gap-4">
//             <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Contact Details</h4>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Address</label><textarea name="address" value={formData.address} onChange={handleChange} className={`${inputClasses} min-h-[80px]`}></textarea></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>City</label><input type="text" name="city" value={formData.city} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Phone</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Mobile</label><input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Email Id</label><input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Remark</label><textarea name="remark" value={formData.remark} onChange={handleChange} className={`${inputClasses} min-h-[80px]`}></textarea></div>
//             <div className="flex flex-row items-center gap-4">
//               <label className={`${labelClasses}`}>Status{requiredLabel}</label>
//               <label className="relative inline-block w-[50px] h-[28px]">
//                 <input type="checkbox" name="status" checked={formData.status} onChange={handleChange} className="opacity-0 w-0 h-0 peer" />
//                 <span className="absolute cursor-pointer top-0 left-0 right-0 bottom-0 bg-gray-300 rounded-full transition-all before:absolute before:h-5 before:w-5 before:left-1 before:bottom-1 before:bg-white before:rounded-full before:transition-all peer-checked:bg-green-500 peer-checked:before:translate-x-[22px]"></span>
//               </label>
//               <span className="font-bold">{formData.status ? 'Active' : 'Inactive'}</span>
//             </div>
//           </div>
//         </div>

//         {/* Right Column */}
//         <div className="flex flex-col gap-6">
//           <div className="flex flex-col gap-4">
//             <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Salary Details</h4>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Basic</label><input type="number" step="0.01" name="basic" value={formData.basic} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>D/A</label><input type="number" step="0.01" name="da" value={formData.da} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>HRA</label><input type="number" step="0.01" name="hra" value={formData.hra} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Allowance</label><input type="number" step="0.01" name="allowance" value={formData.allowance} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Allowance Mode</label><select name="allowanceMode" value={formData.allowanceMode} onChange={handleChange} className={inputClasses}><option>Monthly</option><option>Yearly</option></select></div>
//           </div>
//           <div className="flex flex-col gap-4">
//             <h4 className="pb-2 border-b-2 border-blue-500 text-blue-500 text-md font-semibold">Payment Details</h4>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Payment Mode</label><select name="paymentMode" value={formData.paymentMode} onChange={handleChange} className={inputClasses}><option>Bank Transfer</option><option>UPI</option><option>Cash</option></select></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Gpay & Phonepe No</label><input type="text" name="gpayPhonepeNo" value={formData.gpayPhonepeNo} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Bank Name</label><input type="text" name="bankName" value={formData.bankName} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Branch Name</label><input type="text" name="branchName" value={formData.branchName} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>IFSC Code</label><input type="text" name="ifscCode" value={formData.ifscCode} onChange={handleChange} className={inputClasses} /></div>
//             <div className="flex flex-col gap-1.5"><label className={labelClasses}>Account No</label><input type="text" name="accNo" value={formData.accNo} onChange={handleChange} className={inputClasses} /></div>
//           </div>
//         </div>
//       </div>

//       <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-slate-200">
//         <button type="submit" className="px-3 py-1 text-sm font-medium text-white bg-blue-500 rounded hover:bg-blue-600">
//           Save
//         </button>
//         <button type="button" className="px-3 py-1 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600" onClick={onCancel}>
//           Cancel
//         </button>

//       </div>
//     </form>
//   );
// };

// const EntryPage = ({ onSave, onCancel, initialData }) => {
//   const isEditing = !!initialData;
//   const today = new Date().toISOString().split('T')[0];
//   const newEmployeeTemplate = {
//     employeeCode: '', employeeName: '', gender: 'Male', designation: '', department: '',
//     dateOfBirth: '', dateOfJoin: today, address: '', city: '', phone: '', mobile: '',
//     email: '', remark: '', status: true, basic: 0, da: 0, hra: 0, allowance: 0,
//     allowanceMode: 'Monthly', paymentMode: 'Bank Transfer', gpayPhonepeNo: '',
//     bankName: '', branchName: '', ifscCode: '', accNo: '',
//   };

//   return (
//     <div className="max-w-5xl mx-auto bg-white p-6 sm:p-10 rounded-lg shadow-md">
//       <div className="flex items-center gap-4 mb-8">
//         {/* <button className="w-10 h-10 flex items-center justify-center bg-slate-200 rounded-full transition-colors hover:bg-slate-300" onClick={onCancel} aria-label="Go back to list">
//           <ChevronLeft />
//         </button> */}
//         <h2 className="text-2xl font-bold text-center text-slate-800">{isEditing ? 'Edit Employee' : 'Create New Employee'}</h2>
//       </div>
//       <EmployeeForm
//         initialData={isEditing ? initialData : newEmployeeTemplate}
//         onSave={onSave}
//         onCancel={onCancel}
//       />
//     </div>
//   );
// };



// // --- MAIN APP ---
// export default function App() {
//   const [employees, setEmployees] = useState([]);
//   const [page, setPage] = useState({ view: 'list', data: null });
//   const [view, setView] = useState('card');
  
//   const [isLoading, setIsLoading] = useState(true);

//   const fetchAllEmployees = async () => {
//     setIsLoading(true);
//     try {
//       const data = await api.EmployeeList("Admin");
//       setEmployees(data?.EmployeeListAPIjs?.Table?.map(mapEmployeeFromApi) || []);
//     } catch (error) {
//       console.error(error);
//       alert("Could not fetch employee list.");
//     } finally { setIsLoading(false); }
//   };

//   useEffect(() => { fetchAllEmployees(); }, []);

//   const handleSave = async (employeeData) => {
//     try {
//       const payload = mapEmployeeToApi(employeeData);
//       const response = await api.SaveEmployee(payload);
//       if (response?.SaveEmployeeAPIjs?.Table?.length) {
//         alert('✅ Employee saved successfully!');
//         await fetchAllEmployees();
//         setPage({ view: 'list', data: null });
//       } else throw new Error("Failed to save");
//     } catch (error) {
//       console.error(error);
//       alert('❌ Failed to save employee.');
//     }
//   };

//   const handleEdit = async (employee) => {
//     try {
//       const res = await api.EditEmployee(employee.Eid);
//       if (res?.EditEmployeeAPIjs?.Table?.length) {
//         setPage({ view: 'edit', data: mapEmployeeFromApi(res.EditEmployeeAPIjs.Table[0]) });
//       } else throw new Error("Employee not found");
//     } catch (error) {
//       console.error(error);
//       alert('❌ Could not fetch employee details.');
//     }
//   };

//   const handleAddNew = () => setPage({ view: 'create', data: null });
//   const handleCancel = () => setPage({ view: 'list', data: null });

//   const renderPage = () => {
//     if (isLoading && page.view === 'list') return <div className="text-center p-12">Loading...</div>;
//     switch (page.view) {
//       case 'create':
//       case 'edit': return <EntryPage onSave={handleSave} onCancel={handleCancel} initialData={page.data} />;
//       case 'list':
//       default: return <ListPage employees={employees} view={view} setView={setView} onEdit={handleEdit} onAddNew={handleAddNew}  onBack={handleCancel}/>;
//     }
//   };

//   return (
//     <div className="flex flex-col min-h-screen font-sans bg-slate-100 text-slate-800">
//       <main className="flex-grow p-4 sm:p-8 max-w-7xl mx-auto w-full">
//         {renderPage()}
//       </main>
//     </div>
//   );
// }
